/**
 * Recorder Dock Component (Bottom)
 */

import React, { useState, useRef, useEffect } from "react";
import { useRecorderStore } from "../store/recorderStore";
import { useSessionStore } from "../store/sessionStore";
import { usePlaybackStore } from "../store/playbackStore";
import { RecorderEngine } from "../audio/RecorderEngine";
import { AudioEngine } from "../audio/AudioEngine";
import { InputMeter } from "../audio/InputMeter";
import WAVEncoder from "../audio/WavEncoder";
import { apiClient } from "../api/client";
import JamroomIconButton from "./JamroomIconButton";
import "./RecorderDock.css";

interface RecorderDockProps {
  sessionId: string;
  audioEngine: AudioEngine | null;
  ws?: WebSocket | null;
  rightControls?: React.ReactNode;
}

const inferInputFileKind = (file: File): "wav" | "mp3" | "other" => {
  const mime = (file.type || "").toLowerCase();
  const name = file.name.toLowerCase();
  if (mime.includes("wav") || name.endsWith(".wav")) return "wav";
  if (mime.includes("mpeg") || mime.includes("mp3") || name.endsWith(".mp3")) return "mp3";
  return "other";
};

const getContentTypeForDraftFormat = (format: "wav" | "webm_opus" | "mp3"): string => {
  if (format === "wav") return "audio/wav";
  if (format === "mp3") return "audio/mpeg";
  return "audio/webm;codecs=opus";
};

const mixAudioBufferToMono = (audioBuffer: AudioBuffer): Float32Array => {
  const channels = Math.max(1, audioBuffer.numberOfChannels);
  const frames = audioBuffer.length;
  if (channels === 1) {
    return new Float32Array(audioBuffer.getChannelData(0));
  }

  const channelData: Float32Array[] = [];
  for (let c = 0; c < channels; c += 1) {
    channelData.push(audioBuffer.getChannelData(c));
  }

  const mixed = new Float32Array(frames);
  for (let i = 0; i < frames; i += 1) {
    let sum = 0;
    for (let c = 0; c < channelData.length; c += 1) {
      sum += channelData[c][i];
    }
    mixed[i] = sum / channelData.length;
  }
  return mixed;
};

const transcodeAudioBufferToWav = (
  audioBuffer: AudioBuffer,
  options?: { sampleRate?: number }
): { blob: Blob; sampleRate: number; channels: number } => {
  const targetSampleRate = options?.sampleRate ?? 48000;
  const mono = mixAudioBufferToMono(audioBuffer);
  const resampled = WAVEncoder.resample(mono, audioBuffer.sampleRate, targetSampleRate);
  const encoder = new WAVEncoder({ sampleRate: targetSampleRate, channels: 1, bitDepth: 16 });
  return { blob: encoder.encode(resampled), sampleRate: targetSampleRate, channels: 1 };
};

const formatRecTime = (ms: number): string => {
  const clamped = Math.max(0, Math.floor(ms));
  const totalSec = Math.floor(clamped / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
};

const RecorderDock: React.FC<RecorderDockProps> = ({ sessionId: sessionIdProp, audioEngine, ws, rightControls }) => {
  const {
    isRecording,
    setRecording,
    monitoringEnabled,
    setMonitoring,
    countInBars,
    setCountIn,
    inputDeviceId,
    echoCancellation,
    noiseSuppression,
    autoGainControl,
  } = useRecorderStore();
  const { setPlaying, setPlayheadMs } = usePlaybackStore();

  const { sessionId, drafts, addDraft, removeDraft, addTrack, participantName, permissions, sessionExpired } =
    useSessionStore();
  const recorderRef = useRef<RecorderEngine | null>(null);
  const recordStartOffsetMsRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputMeterRef = useRef<InputMeter | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [recordingStartedAtMs, setRecordingStartedAtMs] = useState<number | null>(null);
  const [recordTick, setRecordTick] = useState(0);
  const [meterEnabled, setMeterEnabled] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const resolvedSessionId = sessionIdProp || sessionId || "";

  useEffect(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
    }

    return () => {
      try {
        inputMeterRef.current?.stop();
      } catch {
        // ignore
      }
      inputMeterRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!currentDraftId) return;
    const exists = drafts.some((draft) => draft.localId === currentDraftId);
    if (!exists) {
      setCurrentDraftId(null);
    }
  }, [currentDraftId, drafts]);

  useEffect(() => {
    if (!isRecording) {
      setRecordingStartedAtMs(null);
      return;
    }

    const id = window.setInterval(() => setRecordTick((v) => v + 1), 250);
    return () => window.clearInterval(id);
  }, [isRecording]);

  // Stop the "mic test" meter when recording begins (avoid double-capturing the mic).
  useEffect(() => {
    if (!isRecording) return;
    if (meterEnabled) setMeterEnabled(false);
    try {
      inputMeterRef.current?.stop();
    } catch {
      // ignore
    }
    inputMeterRef.current = null;
  }, [isRecording, meterEnabled]);

  // Start/stop the mic test meter.
  useEffect(() => {
    if (isRecording) return;
    if (!meterEnabled) {
      try {
        inputMeterRef.current?.stop();
      } catch {
        // ignore
      }
      inputMeterRef.current = null;
      return;
    }

    let cancelled = false;
    const start = async () => {
      try {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext ||
            (window as any).webkitAudioContext)();
        }

        inputMeterRef.current?.stop();
        inputMeterRef.current = new InputMeter(audioContextRef.current!, {
          channels: 1,
          inputDeviceId,
          echoCancellation,
          noiseSuppression,
          autoGainControl,
        });
        await inputMeterRef.current.start();
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to start mic meter:", error);
        inputMeterRef.current = null;
        setMeterEnabled(false);

        const name = (error as any)?.name ? String((error as any).name) : "";
        if (name === "NotAllowedError" || name === "SecurityError") {
          alert("Microphone access was blocked. Allow mic permissions and try again.");
          return;
        }
        if (name === "NotFoundError") {
          alert("No microphone found. Connect an input device and try again.");
          return;
        }
        if (name === "NotReadableError") {
          alert("Microphone is busy/unavailable. Close other apps using it and try again.");
          return;
        }

        alert("Failed to start microphone meter");
      }
    };

    void start();
    return () => {
      cancelled = true;
    };
  }, [autoGainControl, echoCancellation, inputDeviceId, isRecording, meterEnabled, noiseSuppression]);

  // Poll level at a low rate to keep React renders cheap (still feels responsive).
  useEffect(() => {
    const active = isRecording || meterEnabled;
    if (!active) {
      setInputLevel(0);
      return;
    }

    const id = window.setInterval(() => {
      let level = 0;
      if (isRecording && recorderRef.current) {
        level = recorderRef.current.getInputLevel();
      } else if (!isRecording && meterEnabled && inputMeterRef.current) {
        level = inputMeterRef.current.getLevel();
      }

      const next = Number.isFinite(level) ? Math.max(0, Math.min(1, level)) : 0;
      setInputLevel((prev) => prev * 0.72 + next * 0.28);
    }, 60);

    return () => window.clearInterval(id);
  }, [isRecording, meterEnabled]);

  const sendRecordingState = (nextRecording: boolean) => {
    try {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      ws.send(JSON.stringify({ type: "recording_state", isRecording: nextRecording }));
    } catch {
      // ignore
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      }

      // Decode audio
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

      // Create draft
      const draftId = `draft-${Date.now()}`;
      const durationMs = Math.round(audioBuffer.duration * 1000);
      const inputKind = inferInputFileKind(file);

      // If the uploaded file isn't WAV/MP3 (eg m4a, ogg, webm), transcode to WAV for compatibility.
      // This avoids creating "webm_opus" tracks that won't play on iPhone/iPad.
      let draftBlob: Blob = file;
      let draftFormat: "wav" | "webm_opus" | "mp3" = inputKind === "mp3" ? "mp3" : "wav";
      let draftSampleRate = audioBuffer.sampleRate;
      let draftChannels = audioBuffer.numberOfChannels;

      const isUploadCompatible = inputKind === "wav" || inputKind === "mp3";
      if (inputKind === "other") {
        const transcoded = transcodeAudioBufferToWav(audioBuffer, { sampleRate: 48000 });
        draftBlob = transcoded.blob;
        draftFormat = "wav";
        draftSampleRate = transcoded.sampleRate;
        draftChannels = transcoded.channels;
      }

      addDraft({
        localId: draftId,
        displayName: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
        authorName: participantName || "Guest",
        sessionId: resolvedSessionId,
        blob: draftBlob,
        format: draftFormat,
        durationMs,
        sampleRate: draftSampleRate,
        channels: draftChannels,
        offsetMs: 0,
        peaksJson: "{}",
        createdAt: Date.now(),
      });

      if (audioEngine) {
        await audioEngine.loadBuffer(draftId, draftBlob);
      }

      setCurrentDraftId(draftId);
      alert(isUploadCompatible ? "File loaded! Ready to publish." : "File transcoded to WAV for compatibility. Ready to publish.");
    } catch (error) {
      console.error("Failed to load audio file:", error);
      alert("Failed to load audio file");
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRecord = async () => {
    if (isRecording) return;
    if (sessionExpired) {
      alert("Session expired. Recording is disabled.");
      return;
    }
    if (!permissions.canRecord) {
      alert("You do not have permission to record in this session.");
      return;
    }

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
      }

      const wasPlaying = usePlaybackStore.getState().isPlaying;
      let startOffsetMs = usePlaybackStore.getState().playheadMs;

      recorderRef.current = new RecorderEngine(audioContextRef.current, {
        sampleRate: 48000,
        channels: 1,
        monitoringEnabled,
        inputDeviceId,
        echoCancellation,
        noiseSuppression,
        autoGainControl,
      });
      await recorderRef.current.startRecording();

      if (audioEngine && !wasPlaying) {
        await audioEngine.resume();
        audioEngine.play(startOffsetMs);
        setPlaying(true);
      } else {
        startOffsetMs = usePlaybackStore.getState().playheadMs;
      }

      recordStartOffsetMsRef.current = Math.max(0, Math.round(startOffsetMs));
      setRecording(true);
      setRecordingStartedAtMs(Date.now());
      setRecordTick(0);
      sendRecordingState(true);
      setCurrentDraftId(null);
    } catch (error) {
      console.error("Failed to start recording:", error);

      const name = (error as any)?.name ? String((error as any).name) : "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        alert("Microphone access was blocked. Allow mic permissions and try again.");
        return;
      }
      if (name === "NotFoundError") {
        alert("No microphone found. Connect an input device and try again.");
        return;
      }
      if (name === "NotReadableError") {
        alert("Microphone is busy/unavailable. Close other apps using it and try again.");
        return;
      }

      alert("Failed to start recording");
    }
  };

  const handleStop = async (opts?: { silent?: boolean }) => {
    if (!isRecording || !recorderRef.current) return;

    try {
      const recording = await recorderRef.current.stopRecording();
      setRecording(false);
      setRecordingStartedAtMs(null);
      sendRecordingState(false);
      recorderRef.current = null;

      // Create draft
      const draftId = `draft-${Date.now()}`;
      addDraft({
        localId: draftId,
        displayName: "New Recording",
        authorName: participantName || "Guest",
        sessionId: resolvedSessionId,
        blob: recording.blob,
        format: recording.format,
        sampleRate: recording.sampleRate,
        channels: recording.channels,
        durationMs: recording.durationMs,
        offsetMs: recordStartOffsetMsRef.current,
        peaksJson: recording.peaksJson,
        createdAt: Date.now(),
      });

      if (audioEngine) {
        await audioEngine.loadBuffer(draftId, recording.blob);
      }

      setCurrentDraftId(draftId);
    } catch (error) {
      console.error("Failed to stop recording:", error);
      if (!opts?.silent) {
        alert("Failed to save recording");
      }
    }
  };

  useEffect(() => {
    if (!sessionExpired) return;
    if (!isRecording) return;
    void handleStop({ silent: true });
  }, [isRecording, sessionExpired]);

  const handlePublish = async () => {
    if (!currentDraftId || !resolvedSessionId) return;
    if (sessionExpired) {
      alert("Session expired. Publishing is disabled.");
      return;
    }

    const draft = drafts.find((d) => d.localId === currentDraftId);
    if (!draft) return;

    const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

    try {
      let presignRes;
      try {
        // Request presign
        presignRes = await apiClient.presignTrackUpload(resolvedSessionId, {
          format: draft.format,
          contentType: getContentTypeForDraftFormat(draft.format),
          sizeBytes: draft.blob.size,
          durationMs: draft.durationMs,
          sampleRate: draft.sampleRate,
          channels: draft.channels,
        });
      } catch (e) {
        throw new Error(`Presign failed: ${errMsg(e)}`);
      }

      const requiresUpload = presignRes.uploadRequired !== false;
      if (requiresUpload) {
        if (!presignRes.uploadUrl) {
          throw new Error("Upload URL missing");
        }
        try {
          await apiClient.uploadToPresignedUrl(presignRes.uploadUrl, draft.blob);
        } catch (e) {
          throw new Error(`Upload failed: ${errMsg(e)}`);
        }
      }

      let commitRes;
      try {
        // Commit
        commitRes = await apiClient.commitTrack(resolvedSessionId, presignRes.trackId, {
          displayName: draft.displayName,
          authorName: draft.authorName,
          durationMs: draft.durationMs,
          sampleRate: draft.sampleRate,
          channels: draft.channels,
          format: draft.format,
          offsetMs: draft.offsetMs,
          gain: 1,
          peaksKey: presignRes.peaksKey,
        });
      } catch (e) {
        throw new Error(`Commit failed: ${errMsg(e)}`);
      }
      if (commitRes.track) {
        addTrack(commitRes.track);
      }

      // Load audio into AudioEngine immediately
      if (audioEngine) {
        audioEngine.removeTrack(currentDraftId);
        await audioEngine.loadBuffer(presignRes.trackId, draft.blob);
        console.log(`Track loaded into AudioEngine: ${presignRes.trackId}`);
      }

      // Remove from drafts
      removeDraft(currentDraftId);
      setCurrentDraftId(null);

      alert("Track published!");
    } catch (error) {
      console.error("Failed to publish:", error);
      alert(error instanceof Error ? error.message : "Failed to publish track");
    }
  };

  const handlePreview = async () => {
    if (!audioEngine || !currentDraftId) return;
    if (sessionExpired) {
      alert("Session expired. Playback is disabled.");
      return;
    }

    const draft = drafts.find((d) => d.localId === currentDraftId);
    if (!draft) return;

    try {
      await audioEngine.resume();
      await audioEngine.loadBuffer(draft.localId, draft.blob);
      audioEngine.play(draft.offsetMs || 0);
      setPlayheadMs(draft.offsetMs || 0);
      setPlaying(true);
    } catch (error) {
      console.error("Failed to preview draft:", error);
      alert("Failed to preview draft");
    }
  };

  const recordingElapsedMs =
    isRecording && recordingStartedAtMs ? Date.now() - recordingStartedAtMs + recordTick * 0 : 0;

  const recordDisabled = ((!permissions.canRecord || sessionExpired) && !isRecording) || false;

  return (
    <div className="recorder-dock">
      <div className="recorder-controls">
        <JamroomIconButton
          iconId={isRecording ? "jr-stop" : "jr-record"}
          ariaLabel={isRecording ? "Stop recording" : "Start recording"}
          danger
          pressed={isRecording}
          disabled={recordDisabled}
          className={`record-btn ${isRecording ? "recording" : ""}`}
          title={isRecording ? "Stop recording" : sessionExpired ? "Session expired" : "Start recording"}
          onClick={() => {
            if (isRecording) {
              void handleStop();
            } else {
              void handleRecord();
            }
          }}
        />

        {isRecording && (
          <div className="recorder-rec" role="status" aria-label="Recording indicator">
            <span className="recorder-rec-dot" aria-hidden="true" />
            <span className="recorder-rec-label">REC</span>
            <span className="recorder-rec-time">{formatRecTime(recordingElapsedMs)}</span>
          </div>
        )}

        <button 
          className="jr-btn jr-btn--ghost upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={sessionExpired || isRecording}
          title={sessionExpired ? "Session expired" : "Upload audio"}
          aria-label="Upload audio"
        >
          Upload
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />

        {currentDraftId && (
          <>
            <button
              className="jr-btn jr-btn--ghost preview-btn"
              onClick={handlePreview}
              disabled={sessionExpired || isRecording || !audioEngine}
              title={sessionExpired ? "Session expired" : "Preview draft"}
              aria-label="Preview draft"
            >
              Preview
            </button>
            <button
              className="jr-btn jr-btn--primary publish-btn"
              onClick={handlePublish}
              disabled={sessionExpired || isRecording}
              title={sessionExpired ? "Session expired" : "Publish draft"}
              aria-label="Publish draft"
            >
              Publish
            </button>
          </>
        )}
      </div>

      <div className="recorder-settings">
        <div className="recorder-monitor">
          <JamroomIconButton
            iconId="jr-headphones"
            ariaLabel={monitoringEnabled ? "Disable monitoring" : "Enable monitoring"}
            pressed={monitoringEnabled}
            disabled={sessionExpired || isRecording}
            size={36}
            className="recorder-monitor-btn"
            title={
              sessionExpired
                ? "Session expired"
                : isRecording
                  ? "Change monitoring before recording"
                  : monitoringEnabled
                    ? "Disable monitoring"
                    : "Enable monitoring"
            }
            onClick={() => setMonitoring(!monitoringEnabled)}
          />
          <span className="recorder-monitor-label">Monitor</span>
        </div>

        <label>
          Count-in:
          <select value={countInBars} onChange={(e) => setCountIn(Number(e.target.value) as 0 | 1 | 2)}>
            <option value={0}>None</option>
            <option value={1}>1 bar</option>
            <option value={2}>2 bars</option>
          </select>
        </label>
      </div>

      <div className="recorder-right">
        {rightControls}
        <div className="recorder-level-meter" aria-label="Input level meter">
          <JamroomIconButton
            iconId="jr-mic"
            ariaLabel={meterEnabled ? "Stop microphone test" : "Test microphone input"}
            pressed={meterEnabled}
            disabled={isRecording || sessionExpired || !permissions.canRecord}
            size={34}
            className="meter-mic-btn"
            title={
              !permissions.canRecord
                ? "Recording disabled for your role"
                : sessionExpired
                  ? "Session expired"
                  : isRecording
                    ? "Recording in progress"
                    : meterEnabled
                      ? "Stop mic test"
                      : "Test mic input (shows level)"
            }
            onClick={() => setMeterEnabled((v) => !v)}
          />
          <div
            className="meter-bar"
            role="progressbar"
            aria-label="Microphone input level"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(inputLevel * 100)}
            data-active={isRecording || meterEnabled ? "1" : "0"}
            data-hot={inputLevel > 0.92 ? "1" : "0"}
          >
            <div className="meter-fill" style={{ width: `${Math.round(inputLevel * 100)}%` }} />
          </div>
          <span className="meter-label">Level</span>
          {participantName && (
            <div className="recorder-you" title={`You: ${participantName}`}>
              <svg aria-hidden="true" focusable="false" className="recorder-you-icon">
                <use href="#jr-mic" />
              </svg>
              <span className="recorder-you-name">{participantName}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecorderDock;
